import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  BookmarkIcon as BookmarkIconSolid,
} from "@heroicons/react/24/solid";
import {
  BookmarkIcon as BookmarkIconOutline,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { ImageTypeEnum, ModuleI, ModuleStatus } from "../../../types";
import { useGetTopicBookmarks } from "../../../api/queries";
import AddImageModal from "../../../components/AddImageModal";
import Popover from "../../../componentLibrary/Popover";
import Button from "../../../componentLibrary/Button";
import Spinner from "../../../componentLibrary/Spinner";
import BookmarkButton from "../../../components/BookmarkButton";

/** Same Tailwind classes as the status filter circles in this list — reused on draft topic cards. */
export function getModuleStatusSolidBgClass(
  status: ModuleStatus | undefined | null,
): string {
  switch (status) {
    case ModuleStatus.Published:
      return "bg-green-500";
    case ModuleStatus.Draft:
      return "bg-yellow-500";
    case ModuleStatus.Coming_Soon:
      return "bg-purple-400";
    case ModuleStatus.Archived:
      return "bg-gray-400";
    default:
      return "bg-yellow-500";
  }
}

export default function TopicsList({
  modules,
  moduleId,
  setModuleData,
  isEdit,
  setIsEdit,
  canCreateContent = false,
  showAddModulePanel,
  setShowAddModulePanel,
  onModuleSelect,
}: {
  modules: ModuleI[];
  moduleId: string;
  setModuleData: Dispatch<SetStateAction<ModuleI | undefined>>;
  isEdit: boolean;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
  canCreateContent?: boolean;
  showAddModulePanel: boolean;
  setShowAddModulePanel: Dispatch<SetStateAction<boolean>>;
  onModuleSelect?: () => void;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<ModuleStatus | "all">(
    ModuleStatus.Published,
  );
  const { data: bookmarks, isLoading, isError } = useGetTopicBookmarks();
  const [isBookmarked, setIsBookmarked] = useState(false);

  const navigate = useNavigate();

  const statusOrder = {
    [ModuleStatus.Published]: 1,
    [ModuleStatus.Draft]: 2,
    [ModuleStatus.Coming_Soon]: 3,
    [ModuleStatus.Archived]: 4,
  };

  useEffect(() => {
    if (!modules || modules.length === 0) return;
    // Don't auto-select a module when user explicitly chose "Add Module"
    if (showAddModulePanel) return;

    // If there's a moduleId (selected module), set filter to match its status
    // This handles both initial load and after save/refetch
    if (moduleId) {
      const selectedModule = modules.find((m) => m._id === moduleId);
      if (selectedModule) {
        setSelectedStatus(selectedModule.status);
        setModuleData(selectedModule);
        return;
      }
    }

    // No moduleId - select first published module, or fall back to first module
    const firstPublishedModule = modules.find(
      (m) => m.status === ModuleStatus.Published,
    );
    setModuleData(firstPublishedModule || modules[0]);
  }, [modules, moduleId, setModuleData, showAddModulePanel]);

  const filteredAndSortedModules = (
    isBookmarked
      ? modules.filter((module) =>
          bookmarks?.some((bookmark) => bookmark.module?._id === module._id),
        )
      : modules
  )
    .filter(
      (module) => selectedStatus === "all" || module.status === selectedStatus,
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  if (isLoading) return <Spinner className="mx-auto" />;
  if (isError) return <div>Error</div>;

  return (
    <>
      <div>
        <div className="flex flex-row justify-between mb-2">
          <h3>Modules</h3>
          <Popover
            buttonBody={<PlusIcon className="h-6 w-6" />}
            options={[
              {
                label: "Add Module",
                onClick: () => {
                  if (!canCreateContent) return;
                  setShowAddModulePanel(true);
                  setModuleData(undefined);
                  setIsEdit(false);
                },
                disabled: !canCreateContent,
                title: !canCreateContent
                  ? "Ask an admin if you need permission to create modules"
                  : undefined,
              },
              {
                label: "Add Lesson",
                onClick: () => {
                  if (!canCreateContent) return;
                  navigate("/admin/create-lesson");
                },
                disabled: !canCreateContent,
                title: !canCreateContent
                  ? "Ask an admin if you need permission to create lessons"
                  : undefined,
              },
              {
                label: "Add Icon",
                onClick: () => {
                  if (!canCreateContent) return;
                  setIsModalOpen(true);
                },
                disabled: !canCreateContent,
                title: !canCreateContent
                  ? "Ask an admin if you need permission to add icons"
                  : undefined,
              },
            ]}
          />
        </div>
        <div className="flex flex-row justify-between items-center">
          <div className="flex gap-2 mb-4">
            <Button.Circle
              variant="custom"
              className={`w-6 h-6 text-white text-sm font-bold ${
                selectedStatus === "all"
                  ? "ring-2 ring-offset-2 ring-blue-500"
                  : ""
              } bg-blue-500`}
              onClick={() => setSelectedStatus("all")}
            >
              All
            </Button.Circle>
            {Object.keys(statusOrder).map((status) => (
              <Button.Circle
                key={status}
                variant="custom"
                className={`w-6 h-6 text-white text-sm font-bold ${
                  selectedStatus === status
                    ? "ring-2 ring-offset-2 ring-blue-500"
                    : ""
                } ${getModuleStatusSolidBgClass(status as ModuleStatus)}`}
                title={status}
                onClick={() => setSelectedStatus(status as ModuleStatus)}
              >
                {status.charAt(0).toUpperCase()}
              </Button.Circle>
            ))}
          </div>
          {isBookmarked ? (
            <BookmarkIconSolid
              className="w-6 h-6 text-electrum cursor-pointer"
              onClick={() => setIsBookmarked(false)}
            />
          ) : (
            <BookmarkIconOutline
              className="w-6 h-6 text-electrum cursor-pointer"
              onClick={() => setIsBookmarked(true)}
            />
          )}
        </div>
        {filteredAndSortedModules.map((module, index) => (
          <div key={module._id}>
            <div
              className={`py-2 px-4 w-full flex items-center justify-between hover:bg-[#F5F5F5] ${
                moduleId === module._id ? "bg-[#F5F5F5]" : "bg-white"
              }`}
              onClick={() => {
                if (isEdit || !moduleId) {
                  const isConfirmed = confirm(
                    "Are you sure you want to proceed?\nAll changes will be lost.",
                  );
                  if (isConfirmed) {
                    setShowAddModulePanel(false);
                    setModuleData(module);
                    setIsEdit(false);
                    onModuleSelect?.();
                  }
                } else {
                  setShowAddModulePanel(false);
                  setModuleData(module);
                  onModuleSelect?.();
                }
              }}
            >
              <p className="flex-grow">{module.title}</p>
              <div className="flex items-center gap-2">
                <Button.Circle
                  variant="custom"
                  className="p-1.5 hover:bg-electrum-100 focus:outline-none focus:ring-0"
                  title={`Manage toolbox for ${module.title}`}
                  aria-label={`Manage toolbox for ${module.title}`}
                  onClick={() => navigate(`/admin/toolboxes/${module._id}`)}
                >
                  <WrenchScrewdriverIcon
                    className="h-6 w-6 text-electrum shrink-0"
                    aria-hidden
                  />
                </Button.Circle>
                <div
                  className={`w-6 h-6 flex items-center justify-center rounded-full text-white text-sm font-bold ${getModuleStatusSolidBgClass(
                    module.status,
                  )}`}
                  title={module.status}
                >
                  {module.status?.charAt(0).toUpperCase()}
                </div>
                <BookmarkButton module={module} width={6} variant="custom" />
              </div>
            </div>
            {index < filteredAndSortedModules.length - 1 && <hr />}
          </div>
        ))}
      </div>
      <AddImageModal
        type={ImageTypeEnum.ICON}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </>
  );
}
